// BrandModal.jsx — updated for NEW Brand logic
// - Supports: brand image + hero slides + about section + stats + SEO + accent color
// - Uses your shared inputs (TextInput/TextArea) + Phosphor icons
// - Sends hero_slides as ARRAY OF STRINGS (base64 or url) ✅ (backend converts to [{id,image}])
// - Sends about_images as ARRAY OF STRINGS (base64 or url)
// - Sends about_stats as ARRAY [{value,label}]
// - Keeps same drawer style

import React, { useEffect, useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { toast } from "sonner"
import axiosInstance from "../../utils/axios/axiosConfig"
import {
  X,
  Camera,
  UploadSimple,
  Trash,
  Plus,
  CheckSquare,
  Square,
  ImageSquare,
  ArrowUpRight,
} from "phosphor-react"
import { TextInput, TextArea } from "../../components"

const MAX_FILE_MB = 15
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024

const defaultStats = [
  { value: "96%", label: "Satisfied Users" },
  { value: "10,000+", label: "Hair Routines Enhanced" },
  { value: "4.8", label: "Average Rating" },
]

const BrandModal = ({ isOpen, onClose, setBrandToUpdate, BrandToUpdate, setDone, done }) => {
  const [loading, setLoading] = useState(false)

  const [formData, setFormData] = useState({
    // base brand
    image: null,
    name: "",
    description: "",
    slug: "",
    featured: false,

    // SEO
    meta_title: "",
    meta_description: "",

    // dynamic sections
    accent_color: "#C4D457",

    // hero slides (array of base64/url strings)
    hero_slides: [],

    // about section
    about_title: "",
    about_text: "",
    about_images: [],

    // stats array
    about_stats: defaultStats,
  })

  // lock scroll
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden"
    else document.body.style.overflow = "unset"
    return () => (document.body.style.overflow = "unset")
  }, [isOpen])

  const ensureArray = (v) => (Array.isArray(v) ? v : [])

  // map backend hero_slides (can be [{id,image}] or ["url"]) to ["url"]
  const normalizeHeroSlides = (raw) => {
    if (!raw) return []
    if (Array.isArray(raw)) {
      return raw
        .map((s) => {
          if (!s) return null
          if (typeof s === "string") return s
          if (typeof s === "object" && s.image) return s.image
          return null
        })
        .filter(Boolean)
    }
    return []
  }

  // about_images should be ["url"]
  const normalizeAboutImages = (raw) => {
    if (!raw) return []
    if (Array.isArray(raw)) return raw.filter((x) => typeof x === "string" && x.trim())
    return []
  }

  const normalizeStats = (raw) => {
    if (!Array.isArray(raw) || !raw.length) return defaultStats
    return raw.map((s) => ({
      value: typeof s?.value === "string" ? s.value : String(s?.value ?? ""),
      label: typeof s?.label === "string" ? s.label : String(s?.label ?? ""),
    }))
  }

  // preload for update
  useEffect(() => {
    if (!isOpen) return

    if (BrandToUpdate?.id) {
      setFormData((prev) => ({
        ...prev,
        image: BrandToUpdate.image || null,
        name: BrandToUpdate.name || "",
        description: BrandToUpdate.description || "",
        slug: BrandToUpdate.slug || "",
        featured: !!BrandToUpdate.featured,

        meta_title: BrandToUpdate.meta_title || "",
        meta_description: BrandToUpdate.meta_description || "",

        accent_color: BrandToUpdate.accent_color || "#C4D457",

        hero_slides: normalizeHeroSlides(BrandToUpdate.hero_slides),
        about_title: BrandToUpdate.about_title || "",
        about_text: BrandToUpdate.about_text || "",
        about_images: normalizeAboutImages(BrandToUpdate.about_images),
        about_stats: normalizeStats(BrandToUpdate.about_stats),
      }))
    } else {
      setFormData({
        image: null,
        name: "",
        description: "",
        slug: "",
        featured: false,

        meta_title: "",
        meta_description: "",

        accent_color: "#C4D457",

        hero_slides: [],
        about_title: "",
        about_text: "",
        about_images: [],
        about_stats: defaultStats,
      })
    }
  }, [isOpen, BrandToUpdate])

  const setField = (name, value) => {
    setFormData((s) => ({ ...s, [name]: value }))
  }

  // -------- file helpers --------
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = reject
    })

  const validateFile = (file) => {
    if (!file) return false
    if (file.size > MAX_FILE_BYTES) {
      toast.warning(`File size exceeds ${MAX_FILE_MB} MB limit`)
      return false
    }
    if (!file.type?.startsWith("image/")) {
      toast.warning("Please select a valid image")
      return false
    }
    return true
  }

  const handleBrandImageChange = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!validateFile(file)) return

    try {
      const base64 = await fileToBase64(file)
      setFormData((s) => ({ ...s, image: base64 }))
    } catch {
      toast.error("Failed reading image")
    }
  }

  const handleAddHeroSlides = async (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ""
    if (!files.length) return

    const good = files.filter(validateFile)
    if (!good.length) return

    try {
      const base64s = await Promise.all(good.map(fileToBase64))
      setFormData((s) => ({ ...s, hero_slides: [...ensureArray(s.hero_slides), ...base64s] }))
    } catch {
      toast.error("Failed reading hero images")
    }
  }

  const handleAddAboutImages = async (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ""
    if (!files.length) return

    const good = files.filter(validateFile)
    if (!good.length) return

    try {
      const base64s = await Promise.all(good.map(fileToBase64))
      setFormData((s) => ({ ...s, about_images: [...ensureArray(s.about_images), ...base64s] }))
    } catch {
      toast.error("Failed reading about images")
    }
  }

  const removeFromArray = (field, index) => {
    setFormData((s) => {
      const arr = ensureArray(s[field])
      return { ...s, [field]: arr.filter((_, i) => i !== index) }
    })
  }

  // -------- stats handlers --------
  const addStat = () => {
    setFormData((s) => ({ ...s, about_stats: [...ensureArray(s.about_stats), { value: "", label: "" }] }))
  }

  const removeStat = (index) => {
    setFormData((s) => {
      const arr = ensureArray(s.about_stats).filter((_, i) => i !== index)
      return { ...s, about_stats: arr.length ? arr : [{ value: "", label: "" }] }
    })
  }

  const updateStat = (index, key, value) => {
    setFormData((s) => {
      const arr = [...ensureArray(s.about_stats)]
      arr[index] = { ...(arr[index] || { value: "", label: "" }), [key]: value }
      return { ...s, about_stats: arr }
    })
  }

  // -------- submit --------
  const buildPayload = () => {
    // IMPORTANT:
    // - hero_slides is array of strings (base64/url). Backend turns it into [{id,image}]
    // - about_images is array of strings
    // - about_stats is array [{value,label}]
    const payload = {
      name: formData.name,
      slug: formData.slug,
      description: formData.description,
      featured: !!formData.featured,

      image: formData.image,

      meta_title: formData.meta_title,
      meta_description: formData.meta_description,

      accent_color: formData.accent_color,

      hero_slides: ensureArray(formData.hero_slides).filter(Boolean),
      about_title: formData.about_title,
      about_text: formData.about_text,
      about_images: ensureArray(formData.about_images).filter(Boolean),

      about_stats: ensureArray(formData.about_stats).map((s) => ({
        value: String(s?.value ?? ""),
        label: String(s?.label ?? ""),
      })),
    }

    return payload
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!String(formData.name || "").trim()) return toast.error("Name is required")
    if (!String(formData.slug || "").trim()) return toast.error("Slug is required")

    setLoading(true)
    try {
      const payload = buildPayload()

      const res = BrandToUpdate?.id
        ? await axiosInstance.put(`/brands/update/${BrandToUpdate.id}`, payload)
        : await axiosInstance.post("/brands/create", payload)

      toast.success(res?.data?.message || "Saved")
      closeModal()
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong")
    } finally {
      setLoading(false)
    }
  }

  const closeModal = () => {
    setBrandToUpdate?.(null)
    setDone?.(!done)
    onClose?.()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/70" onClick={closeModal} />

          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25 }}
            className="relative ml-auto h-full w-full max-w-xl bg-white dark:bg-neutral-900 border-l border-neutral-200 dark:border-neutral-800 overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-start justify-between gap-3 mb-6">
                <div className="text-start">
                  <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">
                    {BrandToUpdate?.id ? "Update" : "Add"} Brand
                  </h2>
                  <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                    Brand info + SEO + Hero + About section.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeModal}
                  className="h-10 w-10 inline-flex items-center justify-center rounded-xl border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                >
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Brand Image */}
                <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-950/30 p-5">
                  <div className="text-start text-sm font-semibold text-neutral-900 dark:text-white">Brand Image</div>

                  <div className="mt-4 flex flex-col items-center">
                    <div className="relative">
                      <div className="h-32 w-32 overflow-hidden rounded-full border-4 border-white dark:border-neutral-900">
                        {formData.image ? (
                          <img src={formData.image} alt="Brand" className="h-full w-full object-cover" draggable={false} />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-neutral-100 dark:bg-neutral-800">
                            <Camera size={26} className="text-neutral-400" />
                          </div>
                        )}
                      </div>

                      <label
                        htmlFor="brand-photo-upload"
                        className="absolute bottom-0 right-0 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-neutral-900 text-white hover:bg-neutral-700"
                      >
                        <UploadSimple size={18} />
                        <input
                          id="brand-photo-upload"
                          type="file"
                          className="hidden"
                          onChange={handleBrandImageChange}
                          accept="image/*"
                        />
                      </label>
                    </div>

                    {formData.image && (
                      <button
                        type="button"
                        onClick={() => setField("image", null)}
                        className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-rose-600 hover:text-rose-700"
                      >
                        <Trash size={18} />
                        Remove photo
                      </button>
                    )}
                    <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">PNG/JPG. Max {MAX_FILE_MB}MB.</p>
                  </div>
                </div>

                {/* Basic */}
                <TextInput label="Name" name="name" value={formData.name} onChange={(e) => setField("name", e.target.value)} placeholder="e.g. Gelix" required />
                <TextInput label="Slug" name="slug" value={formData.slug} onChange={(e) => setField("slug", e.target.value)} placeholder="e.g. gelix" required />
                <TextArea label="Description" name="description" value={formData.description} onChange={(e) => setField("description", e.target.value)} rows={3} placeholder="Optional description..." />

                {/* Featured */}
                <label className="flex items-start gap-3 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-950/30 p-4 cursor-pointer">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={!!formData.featured}
                    onChange={(e) => setField("featured", e.target.checked)}
                    className="peer absolute h-0 w-0 opacity-0"
                  />
                  <div className="mt-0.5 text-neutral-700 dark:text-neutral-200">
                    {formData.featured ? <CheckSquare size={20} /> : <Square size={20} />}
                  </div>
                  <div className="text-start">
                    <div className="text-sm font-semibold text-neutral-900 dark:text-white">Feature this brand</div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Shows on homepage.</div>
                  </div>
                </label>

                {/* Accent */}
                <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-950/30 p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-start">
                      <div className="text-sm font-semibold text-neutral-900 dark:text-white">Accent Color</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">Used for highlights (e.g. About).</div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-xl border border-neutral-200 dark:border-neutral-800" style={{ background: formData.accent_color || "#C4D457" }} />
                      <input
                        type="color"
                        value={formData.accent_color || "#C4D457"}
                        onChange={(e) => setField("accent_color", e.target.value)}
                        className="h-9 w-10 cursor-pointer bg-transparent"
                        aria-label="Accent Color"
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <TextInput
                      label="Accent Hex"
                      name="accent_color"
                      value={formData.accent_color || ""}
                      onChange={(e) => setField("accent_color", e.target.value)}
                      placeholder="#C4D457"
                    />
                  </div>
                </div>

                {/* SEO */}
                <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-950/30 p-5 space-y-4">
                  <div className="text-start">
                    <div className="text-sm font-semibold text-neutral-900 dark:text-white">SEO</div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">Used for Helmet meta tags.</div>
                  </div>

                  <TextInput
                    label="Meta Title"
                    name="meta_title"
                    value={formData.meta_title}
                    onChange={(e) => setField("meta_title", e.target.value)}
                    placeholder="e.g. Boutique Gelix — Commandez en Toute Confiance"
                  />

                  <TextArea
                    label="Meta Description"
                    name="meta_description"
                    value={formData.meta_description}
                    onChange={(e) => setField("meta_description", e.target.value)}
                    rows={3}
                    placeholder="Short description for SEO..."
                  />
                </div>

                {/* Hero Slides */}
                <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-950/30 p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-start">
                      <div className="text-sm font-semibold text-neutral-900 dark:text-white">Hero Slides</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">Upload multiple images for the hero slider.</div>
                    </div>

                    <label className="inline-flex items-center gap-2 text-xs font-[500] px-3 py-2 rounded-xl bg-neutral-900 text-white cursor-pointer hover:opacity-90">
                      <UploadSimple size={16} />
                      Upload
                      <input type="file" multiple onChange={handleAddHeroSlides} className="hidden" accept="image/*" />
                    </label>
                  </div>

                  {formData.hero_slides?.length ? (
                    <div className="mt-4 grid grid-cols-3 gap-3">
                      {formData.hero_slides.map((img, idx) => (
                        <div key={idx} className="relative rounded-2xl overflow-hidden border border-neutral-200 dark:border-neutral-800 bg-white">
                          <img src={img || "/placeholder.svg"} alt="" className="h-24 w-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeFromArray("hero_slides", idx)}
                            className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/90 hover:bg-white border border-neutral-200"
                            aria-label="Remove hero slide"
                          >
                            <Trash size={16} className="text-rose-600" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-4 rounded-xl border border-dashed border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950/30 p-5 text-center">
                      <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
                        <ImageSquare size={18} className="text-neutral-600 dark:text-neutral-300" />
                      </div>
                      <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">No hero slides.</p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">Add 2–4 images for best look.</p>
                    </div>
                  )}
                </div>

                {/* About */}
                <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-950/30 p-5 space-y-4">
                  <div className="text-start">
                    <div className="text-sm font-semibold text-neutral-900 dark:text-white">About Section</div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">About title/text + 3 images.</div>
                  </div>

                  <TextInput
                    label="About Title"
                    name="about_title"
                    value={formData.about_title}
                    onChange={(e) => setField("about_title", e.target.value)}
                    placeholder="e.g. About"
                  />

                  <TextArea
                    label="About Text"
                    name="about_text"
                    value={formData.about_text}
                    onChange={(e) => setField("about_text", e.target.value)}
                    rows={5}
                    placeholder="Write the About paragraph..."
                  />

                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-neutral-900 dark:text-white">About Images</div>
                    <label className="inline-flex items-center gap-2 text-xs font-[500] px-3 py-2 rounded-xl bg-neutral-900 text-white cursor-pointer hover:opacity-90">
                      <UploadSimple size={16} />
                      Upload
                      <input type="file" multiple onChange={handleAddAboutImages} className="hidden" accept="image/*" />
                    </label>
                  </div>

                  {formData.about_images?.length ? (
                    <div className="grid grid-cols-3 gap-3">
                      {formData.about_images.map((img, idx) => (
                        <div key={idx} className="relative rounded-2xl overflow-hidden border border-neutral-200 dark:border-neutral-800 bg-white">
                          <img src={img || "/placeholder.svg"} alt="" className="h-24 w-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeFromArray("about_images", idx)}
                            className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/90 hover:bg-white border border-neutral-200"
                            aria-label="Remove about image"
                          >
                            <Trash size={16} className="text-rose-600" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950/30 p-5 text-center">
                      <p className="text-sm text-neutral-600 dark:text-neutral-300">No about images.</p>
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-950/30 p-5">
                  <div className="flex items-center justify-between">
                    <div className="text-start">
                      <div className="text-sm font-semibold text-neutral-900 dark:text-white">About Stats</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">Shown under About paragraph.</div>
                    </div>

                    <button
                      type="button"
                      onClick={addStat}
                      className="inline-flex items-center gap-2 text-xs font-[500] px-3 py-2 rounded-xl bg-neutral-900 text-white hover:opacity-90"
                    >
                      <Plus size={16} />
                      Add
                    </button>
                  </div>

                  <div className="mt-4 space-y-3">
                    {(formData.about_stats || []).map((s, idx) => (
                      <div key={idx} className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <TextInput
                            label="Value"
                            name={`stat_${idx}_value`}
                            value={s.value}
                            onChange={(e) => updateStat(idx, "value", e.target.value)}
                            placeholder="e.g. 96%"
                          />
                          <TextInput
                            label="Label"
                            name={`stat_${idx}_label`}
                            value={s.label}
                            onChange={(e) => updateStat(idx, "label", e.target.value)}
                            placeholder="e.g. Satisfied Users"
                          />
                        </div>

                        <div className="flex justify-end mt-3">
                          <button
                            type="button"
                            onClick={() => removeStat(idx)}
                            className="inline-flex items-center gap-2 text-xs font-[500] px-3 py-2 rounded-xl bg-rose-500/10 text-rose-700 hover:bg-rose-500/15 border border-rose-500/20"
                          >
                            <Trash size={16} />
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#2B2B35] rounded-xl hover:bg-cyan-400 disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <span className="animate-spin inline-block size-4 border-[2px] border-current border-t-transparent rounded-full" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Plus size={18} />
                        Save
                      </>
                    )}
                  </button>
                </div>

                {/* Mini hint */}
                {BrandToUpdate?.id ? (
                  <div className="pt-1 text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
                    <ArrowUpRight size={14} />
                    Updating keeps existing URLs unless you upload new images.
                  </div>
                ) : null}
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default BrandModal
